import { Vue, Component, Prop } from 'vue-property-decorator';
import { mapGetters, mapState } from 'vuex';
import { MultiLanguage, TurnWithReferences } from '@/Domain/Entities';
import { Constants, ProcessPhases } from '@/Domain/enum';
import { IProcessReference } from '@/Domain/interfaces/Process/IProcessReference';
import { ITurnWithReferences } from '@/Domain/interfaces/Process/ITurnWithReference';
import { SdModal } from '@/Components';
import TurnPreferencesUploadFile from '../TurnPreferencesUploadFile/TurnPreferencesUploadFile';

@Component({
  name: 'turn-preference',
  components: { SdModal, TurnPreferencesUploadFile },
  computed: {
    ...mapState('languagesStore', { currentLanguage: 'current' }),
    ...mapGetters('languagesStore', { availableLanguages: 'getTranslatableLanguages' }),
    ...mapState('featureFlagsStore', ['multiLanguage'])
  }
})

export default class TurnPreference extends Vue {
  @Prop() submitted!: boolean;
  @Prop() isDisabled!: boolean;
  @Prop() processIsUsed!: boolean;
  @Prop() turnsWithReference!: TurnWithReferences;
  @Prop() processPhase!: number;

  currentLanguage!: any;
  availableLanguages!: any;
  Constants = Constants;
  tabActiveLanguage = Constants.language.ES_ES;
  showModalClearExtraVacanciesConfirmation: boolean = false;
  processPhases = ProcessPhases;
  showUploadSlideOver: boolean = false;
  $refs: any = {
    extraVacanciesInputs: HTMLElement
  };

  get turns() {
    return this.turnsWithReference ? this.turnsWithReference.turns : [];
  }

  get turnActive() {
    return this.turnsWithReference ? this.turnsWithReference.turnActive : { references: [] as IProcessReference[], vacancies: 0, type: 0, total: 0, isExtraVacanciesActive: false };
  }

  get totalVacanciesAllTurns() {
    return this.turns.reduce((acc: number, turn: ITurnWithReferences) => acc + turn.total, 0);
  }
  
  get isDisabledAddRefButton() {
    return this.turnsWithReference.anyTurnHasVacancies();
  }

  get isDisabledAddVacanciesInput() {
    return this.turnsWithReference.anyTurnHasReferences() || this.isDisabled;
  }

  get turnsWithErrors() {
    return this.turnsWithReference.turns.map(turn => {
      if (this.submitted && !this.turnsWithReference.isTurnValid(turn, this.availableLanguages)) {
        return turn.type;
      }
      return undefined;
    }).filter(turnType => turnType !== undefined);
  }

  get showLinkTurnPreferenceImport() {
    return !this.processIsUsed &&  !this.isDisabledAddRefButton;
  }

  get showAddExtraVacanciesButton() {
    return this.processIsUsed && !this.turnActive!.isExtraVacanciesActive;
  }

  get showClearExtraVacanciesButton() {
    return this.processIsUsed && this.turnActive!.isExtraVacanciesActive;
  }

  get showExtraVacanciesReferencesInputs() {
    return this.showClearExtraVacanciesButton && this.turnActive!.references.length > 0;
  }

  get showExtraVacanciesTurnInput() {
    return this.showClearExtraVacanciesButton && this.turnActive!.vacancies > 0;
  }

  get extraVacanciesInputMin() {
    return this.turnsWithReference.turnHasExtraVacancies(this.turnsWithReference.turnActive!) ? undefined : 1;
  }

  changeTab(type: number) {
    this.$emit('change-active-turn', type);
  }

  changeIsExtraVacanciesActive() {
    this.$emit('change-extra-vacancies-active');
  }

  addReference() {
    if (this.isDisabledAddRefButton) {
      return;
    }
    
    this.$emit('add-reference');
  }

  removeReference(indexToRemove: number) {
    this.$emit('remove-reference', indexToRemove);
  }

  onVacancieChange(event) {
    const valueToSend = event.target.value !== '' ? event.target.value : 0;
    this.$emit('update-turn', valueToSend);
  }

  onAddExraVacanciesInput(value: any) {
    const valueToSend = value !== '' ? value : 0;
    this.$emit('update-extra-vacancies', { value: valueToSend, turnType: this.turnActive!.type });
  }

  onReferenceMultiLanguageInput(index: number, value: any) {
    const referenceToUpdate = this.turnActive!.references[index];
    const reference = { ...referenceToUpdate, description: new MultiLanguage({ ...referenceToUpdate.description, [this.tabActiveLanguage]: value }) };
    this.updateReference({ reference, index });
  }

  onReferenceInput(index: number, propName: string, value: any) {
    const reference = { ...this.turnActive!.references[index], [propName]: value };
    this.updateReference({ reference, index });
  }

  updateReference(updateEmitData: { reference: IProcessReference, index: number }) {
    this.$emit('update-reference', updateEmitData);
  }

  onClickAddExtraVacancies() {
    if (this.processPhase >= this.processPhases.enum.RANKING) {
      return;
    }

    this.changeIsExtraVacanciesActive();
  }

  onClickClearExtraVacancies() {
    if (this.processPhase >= this.processPhases.enum.RANKING) {
      return;
    }

    this.showModalClearExtraVacanciesConfirmation = true;
  }

  confirmClearExtraVacancies() {
    this.changeIsExtraVacanciesActive();
    this.showModalClearExtraVacanciesConfirmation = false;

    if (this.turnActive && this.turnActive.references.length > 0) {
      this.turnActive.references.forEach((ref, index) => {
        ref.totalExtraVacancies = 0;
        this.updateReference({ reference: ref, index });
      });
      return;
    }
    this.onAddExraVacanciesInput(0);
  }

  onExtraVacanciesInputChange() {
    if (this.extraVacanciesInputMin !== undefined) {
      return;
    }

    this.$refs.extraVacanciesInputs.forEach(component => {
      if (component.$el.classList.contains('error')) {
        component.$el.classList.remove('error');
      }
    });
  }
  updatedTurnReferences() {
    this.$emit('updatedTurnReferences');
  }

  mounted() {
    this.tabActiveLanguage = this.currentLanguage;
  }
}
